import { PropsWithChildren, useEffect, useState } from 'react';

import { ITransaction } from '@/entities/transaction';
import { useTransactionsController } from '@/entities/transaction/model';

import { ICurrencyAccount, cn } from '@/shared/lib';
import {
  Button,
  DashboardDialog,
  Header,
  SelectedRangeDate,
  Skeleton,
  Typography,
} from '@/shared/ui';

import { FINAL_STATUSES } from '../config';
import { TransactionsTypesFilterItem } from '../model/types';
import { EmptyState } from './empty-state';
import { GroupedTransactions } from './grouped-transactions';
import { TransactionInProcess } from './transaction-in-process';

interface TransactionsModalProps extends PropsWithChildren {
  transactions: ITransaction[];
  showAllProcessTransactions?: boolean;
  renderTriggerAsChild?: boolean;
  triggerClassName?: string;
}

const TransactionsModalContent = ({
  showAllProcessTransactions,
}: TransactionsModalProps) => {
  const [expanded, setExpanded] = useState(showAllProcessTransactions);
  const { transactions, search, onSearch, setFilters, reset, isLoading } =
    useTransactionsController();
  const [date, setDate] = useState<SelectedRangeDate | null>(null);
  const [currencies, setCurrencies] = useState<ICurrencyAccount[]>([]);
  const [types, setTypes] = useState<TransactionsTypesFilterItem[]>([]);

  const handleDate = (value: SelectedRangeDate | null) => {
    setDate(value);
    setFilters({
      dateRange:
        value?.start && value?.end
          ? { from: value.start.toISOString(), to: value.end.toISOString() }
          : undefined,
    });
  };

  const handleCurrencies = (value: ICurrencyAccount[]) => {
    setCurrencies(value);
    setFilters({
      currencyIds: value.length ? value.map((i) => i?.currencyId) : undefined,
    });
  };

  const handleTypes = (value: TransactionsTypesFilterItem[]) => {
    setTypes(value);
    setFilters({
      directions: value.length ? value.map((i) => i.type) : undefined,
    });
  };

  useEffect(() => reset, [reset]);

  const toggleExpanded = () => {
    setExpanded((prev) => !prev);
  };

  const transactionsInProcess = transactions.filter(
    (transaction) => !FINAL_STATUSES.includes(transaction.status),
  );

  const transactionsNotInProcess = transactions.filter((transaction) =>
    FINAL_STATUSES.includes(transaction.status),
  );

  const displayedTransactionsInProcess = expanded
    ? transactionsInProcess
    : transactionsInProcess.slice(0, 2);
  const isEmpty =
    displayedTransactionsInProcess.length === 0 &&
    transactionsNotInProcess.length === 0;
  return (
    <div className="flex w-full flex-col gap-3">
      <div className="relative h-12 overflow-y-auto">
        {/* <div className="absolute flex h-full w-auto min-w-full items-center justify-center gap-1 py-2">
          <Search value={search} setValue={onSearch} />
          <DateFilter selectedDate={date} setSelectedDate={handleDate} />
          <CurrencyFilter
            selectedCurrencies={currencies}
            onCurrencyFilterChange={handleCurrencies}
          />
          <TypesFilter
            selectedTypes={types}
            onTypesFilterChange={handleTypes}
          />
        </div> */}
      </div>
      {isLoading ? (
        <div className="mt-4 flex w-full flex-1 flex-col gap-6 overflow-auto p-5">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="my-2 w-full">
              <div className="flex w-full items-center gap-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="w-full">
                  <Skeleton className="h-5 w-full" />
                  <Skeleton className="mt-1 h-5 w-[50%]" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-4 w-full flex-1 overflow-auto">
          {displayedTransactionsInProcess.length > 0 && (
            <div className="rounded-[32px] bg-base-300 p-5 pb-[10px]">
              <Header
                title={`In process • ${String(transactionsInProcess.length)}`}
                action={{
                  as: 'button',
                  onClick: toggleExpanded,
                  label: expanded ? 'See less' : 'See all',
                }}
              />
              {displayedTransactionsInProcess.map((transaction, index) => (
                <TransactionInProcess
                  transaction={transaction}
                  key={transaction.id}
                  className={cn({ 'pt-7': index === 0 })}
                />
              ))}
            </div>
          )}
          <GroupedTransactions transactions={transactionsNotInProcess} />
          {isEmpty && <EmptyState />}
        </div>
      )}
    </div>
  );
};

export const TransactionsModal = ({
  children,
  transactions,
  showAllProcessTransactions,
  renderTriggerAsChild = true,
  triggerClassName,
}: TransactionsModalProps) => {
  return (
    <DashboardDialog
      title="transactions"
      titleProps={{ className: 'h-[38px]' }}
      size="lg"
      modalProps={{
        classNames: { wrapper: '!max-w-[630px] w-full' },
      }}
      height="fixed"
      content={
        <TransactionsModalContent
          transactions={transactions}
          showAllProcessTransactions={showAllProcessTransactions}
        />
      }
      contentClassName="pb-0 outline-none"
      // serviceButton={{
      //   icon: <DownloadIcon className="min-h-6 min-w-6" />,
      // }}
      animationProps={{ isFirst: true }}
      triggerProps={{
        asChild: renderTriggerAsChild,
        className: triggerClassName,
      }}
    >
      {children ?? (
        <Button
          variant="blue-link"
          className={cn('w-full p-0', triggerClassName)}
        >
          <Typography variant="14-m">See all</Typography>
        </Button>
      )}
    </DashboardDialog>
  );
};
